import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router';
import { Boxes, Building2, Mail, Pencil, Plus, Users } from 'lucide-react';
import { api, ApiError } from '../api/cliente.js';
import { Botao } from '../componentes/ui/Botao.js';
import { Modal } from '../componentes/ui/Modal.js';
import { Pill, tomDeStatus } from '../componentes/ui/Pill.js';

interface Operadora {
  id: string;
  nome: string;
  asn: string | null;
  contatoNoc: string | null;
  dominioReverso: string | null;
  status: string;
  blocos: number;
  usuarios: number;
}

export function Operadoras() {
  const qc = useQueryClient();
  const [novaAberta, setNovaAberta] = useState(false);
  const [editando, setEditando] = useState<Operadora | null>(null);
  const recarregar = () => void qc.invalidateQueries({ queryKey: ['operadoras'] });

  const { data: operadoras = [], isLoading } = useQuery({
    queryKey: ['operadoras'],
    queryFn: () => api.get<Operadora[]>('/operadoras'),
  });

  if (isLoading) return <Esqueleto />;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-[13px] text-texto-suave">
          {operadoras.length} {operadoras.length === 1 ? 'operadora' : 'operadoras'} ·{' '}
          {operadoras.reduce((s, o) => s + o.blocos, 0)} blocos no total
        </p>
        <Botao variante="primario" iconeEsquerda={<Plus size={16} />} onClick={() => setNovaAberta(true)}>
          Nova operadora
        </Botao>
      </div>

      {operadoras.length === 0 ? (
        <Vazio aoCriar={() => setNovaAberta(true)} />
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {operadoras.map((o) => (
            <Cartao key={o.id} operadora={o} aoEditar={() => setEditando(o)} />
          ))}
        </div>
      )}

      {novaAberta && (
        <ModalNova aoFechar={() => setNovaAberta(false)} aoCriar={recarregar} />
      )}
      {editando && (
        <ModalEditar
          operadora={editando}
          aoFechar={() => setEditando(null)}
          aoSalvar={recarregar}
        />
      )}
    </div>
  );
}

function Cartao({
  operadora: o,
  aoEditar,
}: {
  operadora: Operadora;
  aoEditar: () => void;
}) {
  return (
    <article className="rounded-card border border-borda bg-superficie p-5 transition-shadow hover:shadow-hover">
      <div className="flex items-start gap-3">
        {/* O handoff prevê logo enviável por operadora. Enquanto o upload não
            existe, as iniciais evitam um espaço vazio e já identificam. */}
        <span className="grid size-11 shrink-0 place-items-center rounded-avatar bg-tint text-[15px] font-extrabold text-marca">
          {iniciais(o.nome)}
        </span>
        <div className="min-w-0 flex-1">
          <p className="truncate text-[15px] font-extrabold leading-tight">{o.nome}</p>
          <p className="font-mono text-[12px] text-texto-suave">{o.asn ?? 'ASN não informado'}</p>
        </div>
        <Pill tom={tomDeStatus(o.status)}>{o.status.toLowerCase()}</Pill>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-3">
        <Metrica Icone={Boxes} valor={o.blocos} rotulo={o.blocos === 1 ? 'bloco' : 'blocos'} />
        <Metrica Icone={Users} valor={o.usuarios} rotulo={o.usuarios === 1 ? 'usuário' : 'usuários'} />
      </div>

      {o.contatoNoc && (
        <p className="mt-3 flex items-center gap-1.5 truncate text-[12.5px] text-texto-suave">
          <Mail size={13} className="shrink-0" />
          {o.contatoNoc}
        </p>
      )}

      {/* O domínio do reverso decide onde o registro A é publicado. Vazio usa
          o da Consult — e é isso que a maioria vai querer. */}
      <p className="mt-3 truncate text-[11.5px] text-texto-mudo">
        reverso em{' '}
        <span className="font-mono">{o.dominioReverso ?? 'rev.consultnetworks.com.br'}</span>
      </p>

      <div className="mt-4 flex gap-1.5">
        <Link
          to={`/blocos?operadora=${o.id}`}
          className="flex h-9 flex-1 items-center justify-center rounded-controle border border-borda text-[13px] font-bold text-texto-medio hover:border-marca hover:text-marca"
        >
          Ver blocos
        </Link>
        <button
          onClick={aoEditar}
          aria-label={`Editar ${o.nome}`}
          className="grid size-9 place-items-center rounded-controle border border-borda text-texto-suave hover:border-marca hover:text-marca"
        >
          <Pencil size={15} />
        </button>
      </div>
    </article>
  );
}

function ModalEditar({
  operadora: o,
  aoFechar,
  aoSalvar,
}: {
  operadora: Operadora;
  aoFechar: () => void;
  aoSalvar: () => void;
}) {
  const [form, setForm] = useState({
    nome: o.nome,
    asn: o.asn ?? '',
    contatoNoc: o.contatoNoc ?? '',
    dominioReverso: o.dominioReverso ?? '',
    status: o.status,
  });
  const [confirmandoRemocao, setConfirmandoRemocao] = useState(false);

  const salvar = useMutation({
    mutationFn: () =>
      api.patch(`/operadoras/${o.id}`, {
        nome: form.nome,
        asn: form.asn.trim() || null,
        contatoNoc: form.contatoNoc.trim() || null,
        dominioReverso: form.dominioReverso.trim() || null,
        status: form.status,
      }),
    onSuccess: () => {
      aoSalvar();
      aoFechar();
    },
  });

  const remover = useMutation({
    mutationFn: () => api.delete(`/operadoras/${o.id}`),
    onSuccess: () => {
      aoSalvar();
      aoFechar();
    },
  });

  return (
    <Modal
      aberto
      aoFechar={aoFechar}
      titulo={`Editar ${o.nome}`}
      rodape={
        <>
          <Botao
            variante="perigo"
            className="mr-auto"
            onClick={() => (confirmandoRemocao ? remover.mutate() : setConfirmandoRemocao(true))}
            carregando={remover.isPending}
          >
            {confirmandoRemocao ? 'Confirmar exclusão' : 'Excluir'}
          </Botao>
          <Botao onClick={aoFechar}>Cancelar</Botao>
          <Botao
            variante="primario"
            onClick={() => salvar.mutate()}
            carregando={salvar.isPending}
            disabled={form.nome.trim().length < 2}
          >
            Salvar
          </Botao>
        </>
      }
    >
      <div className="space-y-4">
        <Campo
          rotulo="Nome"
          valor={form.nome}
          aoMudar={(v) => setForm((f) => ({ ...f, nome: v }))}
        />
        <div className="grid gap-4 sm:grid-cols-2">
          <Campo
            rotulo="ASN"
            valor={form.asn}
            aoMudar={(v) => setForm((f) => ({ ...f, asn: v }))}
            mono
          />
          <label className="block">
            <span className="mb-1.5 block text-[11px] font-bold uppercase tracking-[0.6px] text-texto-suave">
              Status
            </span>
            <select
              value={form.status}
              onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
              className="h-11 w-full rounded-controle border border-borda px-3 text-[13.5px] focus:border-marca focus:outline-none"
            >
              <option value="ATIVO">Ativo</option>
              <option value="SUSPENSO">Suspenso</option>
              <option value="INATIVO">Inativo</option>
            </select>
          </label>
        </div>
        <Campo
          rotulo="Contato do NOC"
          valor={form.contatoNoc}
          aoMudar={(v) => setForm((f) => ({ ...f, contatoNoc: v }))}
          mono
        />
        <Campo
          rotulo="Domínio do reverso"
          valor={form.dominioReverso}
          aoMudar={(v) => setForm((f) => ({ ...f, dominioReverso: v }))}
          placeholder="rev.consultnetworks.com.br"
          dica="Onde o registro A é criado junto com o PTR. Vazio usa o domínio da Consult. Use um subdomínio que a operadora delegue aos nossos nameservers (rev.dominiodela.com.br), nunca o domínio raiz dela."
          mono
        />

        {confirmandoRemocao && !remover.error && (
          <p className="rounded-chip bg-erro-fundo px-3 py-2 text-[12.5px] font-semibold text-erro">
            Clique em "Confirmar exclusão" para remover. Só é possível se não houver blocos nem
            usuários vinculados.
          </p>
        )}

        {(salvar.error || remover.error) && (
          <p className="rounded-chip bg-erro-fundo px-3 py-2 text-[12.5px] font-semibold text-erro">
            {((salvar.error ?? remover.error) as ApiError).descricao}
          </p>
        )}
      </div>
    </Modal>
  );
}

function Metrica({
  Icone,
  valor,
  rotulo,
}: {
  Icone: typeof Boxes;
  valor: number;
  rotulo: string;
}) {
  return (
    <div className="rounded-chip bg-borda-tenue px-3 py-2">
      <p className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.5px] text-texto-suave">
        <Icone size={12} />
        {rotulo}
      </p>
      <p className="mt-0.5 font-mono text-[18px] font-extrabold leading-none">{valor}</p>
    </div>
  );
}

function ModalNova({ aoFechar, aoCriar }: { aoFechar: () => void; aoCriar: () => void }) {
  const [form, setForm] = useState({ nome: '', asn: '', contatoNoc: '' });

  const criar = useMutation({
    mutationFn: () =>
      api.post('/operadoras', {
        nome: form.nome,
        // Campos opcionais vão como undefined quando vazios: string vazia
        // passaria pela validação de e-mail do zod e gravaria lixo.
        asn: form.asn.trim() || undefined,
        contatoNoc: form.contatoNoc.trim() || undefined,
      }),
    onSuccess: () => {
      aoCriar();
      aoFechar();
    },
  });

  return (
    <Modal
      aberto
      aoFechar={aoFechar}
      titulo="Nova operadora"
      descricao="Depois de cadastrada, você pode importar os blocos de IP dela."
      rodape={
        <>
          <Botao onClick={aoFechar}>Cancelar</Botao>
          <Botao
            variante="primario"
            onClick={() => criar.mutate()}
            carregando={criar.isPending}
            disabled={form.nome.trim().length < 2}
          >
            Cadastrar
          </Botao>
        </>
      }
    >
      <div className="space-y-4">
        <Campo
          rotulo="Nome"
          valor={form.nome}
          aoMudar={(v) => setForm((f) => ({ ...f, nome: v }))}
          placeholder="NorteFibra Telecom"
          autoFocus
        />
        <Campo
          rotulo="ASN"
          valor={form.asn}
          aoMudar={(v) => setForm((f) => ({ ...f, asn: v }))}
          placeholder="AS264999"
          dica="Opcional. Formato livre — cada operadora informa de um jeito."
          mono
        />
        <Campo
          rotulo="Contato do NOC"
          valor={form.contatoNoc}
          aoMudar={(v) => setForm((f) => ({ ...f, contatoNoc: v }))}
          placeholder="noc@operadora.com.br"
          dica="Opcional. E-mail para acionar em caso de problema no reverso."
          mono
        />

        {criar.error && (
          <p className="rounded-chip bg-erro-fundo px-3 py-2 text-[12.5px] font-semibold text-erro">
            {(criar.error as ApiError).mensagem}
          </p>
        )}
      </div>
    </Modal>
  );
}

function Campo({
  rotulo,
  valor,
  aoMudar,
  placeholder,
  dica,
  mono,
  autoFocus,
}: {
  rotulo: string;
  valor: string;
  aoMudar: (v: string) => void;
  placeholder?: string;
  dica?: string;
  mono?: boolean;
  autoFocus?: boolean;
}) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-[11px] font-bold uppercase tracking-[0.6px] text-texto-suave">
        {rotulo}
      </span>
      <input
        value={valor}
        onChange={(e) => aoMudar(e.target.value)}
        placeholder={placeholder}
        autoFocus={autoFocus}
        spellCheck={false}
        className={`h-11 w-full rounded-controle border border-borda px-3 text-[14px] focus:border-marca focus:outline-none ${
          mono ? 'font-mono text-[13.5px]' : ''
        }`}
      />
      {dica && <span className="mt-1 block text-[12px] text-texto-suave">{dica}</span>}
    </label>
  );
}

function Vazio({ aoCriar }: { aoCriar: () => void }) {
  return (
    <div className="rounded-card border border-borda bg-superficie px-6 py-14 text-center">
      <div className="mx-auto mb-3 grid size-12 place-items-center rounded-card bg-tint text-marca">
        <Building2 size={24} />
      </div>
      <p className="text-[15px] font-extrabold">Nenhuma operadora cadastrada</p>
      <p className="mx-auto mt-1 max-w-[400px] text-[13px] text-texto-suave">
        A operadora é a dona dos blocos de IP. Cadastre a primeira para começar a importar
        prefixos e gerenciar os reversos.
      </p>
      <Botao variante="primario" className="mt-5" onClick={aoCriar}>
        Cadastrar operadora
      </Botao>
    </div>
  );
}

function iniciais(nome: string): string {
  const partes = nome.trim().split(/\s+/);
  const a = partes[0]?.[0] ?? '';
  const b = partes.length > 1 ? (partes[1]?.[0] ?? '') : '';
  return (a + b).toUpperCase();
}

function Esqueleto() {
  return (
    <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
      {[0, 1, 2].map((i) => (
        <div key={i} className="h-56 animate-pulse rounded-card border border-borda bg-superficie" />
      ))}
    </div>
  );
}
